import IonIcon from '@reacticons/ionicons'
import cn from 'classnames'
import { useRouter } from 'next/router'
import React, { Fragment, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { CompatibleLink } from 'src/components/CompatibleLink/CompatibleLink'
import { IGetTopicsForHeaderQuery, useGetTopicsForHeaderQuery } from 'src/graphql/generated/hooks'
import { AppState } from 'src/store/types'
import { FeaturesType } from 'src/types'

import { Avatar } from '../Avatar/Avatar'
import CartSubContent from '../Cart/CartSubContent'
import { HtmlContentViewer } from '../HtmlContentViewer/HtmlContentViewer'
import { PremiumModal } from '../PremiumModal'

interface DottedDataListProps {
  title?: string
  isSecurity?: boolean
  isDataHostingLocations?: boolean
  isValueProposition?: boolean
  isServiceOfferings?: boolean
  features: FeaturesType[]
  colValue?: number
  isFounder?: boolean
}

/**
 * Primary UI component for user interaction
 */
export const DottedDataList: React.FC<DottedDataListProps> = ({
  title,
  isSecurity,
  isDataHostingLocations,
  isValueProposition,
  isServiceOfferings,
  features = [],
  colValue,
  isFounder,
}) => {
  const { isPremium } = useSelector((state: AppState) => state.authInfo)
  const [isPremiumUser, setIsPremiumUser] = useState<boolean>(false)
  const [isPremiumModalVisible, setIsPremiumModalVisible] = useState(false)

  const [isSecurityData, setIsSecurityData] = useState<boolean>(false)
  const [isDHLocationsData, setIsDHLocationsData] = useState<boolean>(false)
  const [isVPropositionData, setIsVPropositionData] = useState<boolean>(false)

  const router = useRouter()

  let topics: IGetTopicsForHeaderQuery | undefined = {}

  const { data } = useGetTopicsForHeaderQuery({ fetchPolicy: 'cache-first' })

  const findTopic = (subTopicName: string) => {
    const find = topics?.topics?.data.filter((item) => {
      const subTopics = item?.attributes?.subTopics?.data
      return subTopics?.some((subTopic) => subTopic?.attributes?.name === subTopicName)
    })

    return find && find.length > 0
      ? `/topics/${find[0]?.attributes?.slug}/${
          find[0].attributes?.subTopics?.data.filter((item) => item.attributes?.name === subTopicName)[0]?.attributes
            ?.slug
        }`
      : ''
  }
  const randomNum = Math.floor(Math.random() * 1000) // Generates a number between 0 and 999

  useEffect(() => {
    if (isPremium) setIsPremiumUser(isPremium)
  }, [isPremium])

  useEffect(() => {
    if (isSecurity) setIsSecurityData(isSecurity)
    if (isDataHostingLocations) setIsDHLocationsData(isDataHostingLocations)
    if (isValueProposition) setIsVPropositionData(isValueProposition)
  }, [isSecurity, isDataHostingLocations, isValueProposition])

  useEffect(() => {
    if (isServiceOfferings) {
      topics = data
    }
  }, [isServiceOfferings])

  return (
    <div className={cn(`${colValue && 'grid grid-cols-2'} mt-[13px]`)}>
      {features.map((feature: any, index: number) => {
        return (
          <Fragment>
            <PremiumModal
              origin={router.asPath}
              isOpen={isPremiumModalVisible}
              onClose={() => setIsPremiumModalVisible(false)}
            />
            {isServiceOfferings ? (
              <CompatibleLink href={findTopic(feature?.name)}>
                <CartSubContent
                  key={index}
                  type="list"
                  className={cn('mb-[0.125rem] ml-[24px]', {
                    '!text-base': isServiceOfferings,
                    '!text-base underline': findTopic(feature?.name),
                  })}
                >
                  {feature?.name}
                </CartSubContent>
              </CompatibleLink>
            ) : (
              <Fragment>
                {isSecurityData || isDHLocationsData || isVPropositionData ? (
                  <Fragment>
                    {isPremiumUser && isPremiumUser === true ? (
                      <div
                        className={`flex flex-row ml-[24px] ${title === 'Value Proposition' ? 'value-proposition' : ''}`}
                      >
                        <IonIcon name="lock-open" className="mr-1 text-navy flex-shrink-0" />
                        {title === 'Value Proposition' ? (
                          <HtmlContentViewer html={feature?.name} />
                        ) : (
                          <div className="text-[15px] leading-[18px] tracking-normal font-primary">{feature?.name}</div>
                        )}
                      </div>
                    ) : (
                      <div
                        key={randomNum + 'my-2'}
                        className="my-2 select-none cursor-pointer flex flex-row"
                        onClick={() => setIsPremiumModalVisible(true)}
                      >
                        <IonIcon key={randomNum + 'lock-closed'} name="lock-closed" className="text-navy" />
                        <div
                          key={randomNum + 'blur-sm'}
                          className="blur-sm text-[15px] leading-[18px] tracking-normal font-primary ml-[12px] bg-gray-300"
                        >
                          {title}
                        </div>
                      </div>
                    )}
                  </Fragment>
                ) : (
                  <>
                    {title &&
                    [
                      'Latest Funding Round',
                      'Total Funding Amount',
                      'Value Proposition',
                      'Security',
                      'Data Hosting Locations',
                    ].includes(title) ? (
                      <div
                        key={randomNum + 'my-2'}
                        className="my-2 select-none cursor-pointer flex flex-row"
                        onClick={() => setIsPremiumModalVisible(true)}
                      >
                        <IonIcon key={randomNum + 'lock-closed'} name="lock-closed" className="text-navy" />
                        <div
                          key={randomNum + 'blur-sm'}
                          className="blur-sm text-[15px] leading-[18px] tracking-normal font-primary ml-[12px] bg-gray-300"
                        >
                          {title}
                        </div>
                      </div>
                    ) : (
                      <CartSubContent
                        key={index}
                        type={isFounder ? 'founder' : 'list'}
                        className="mb-[0.125rem] ml-[24px]"
                      >
                        {!feature?.description ? (
                          <>
                            {isFounder && (
                              <Avatar
                                size="27px"
                                initialsTextSize="12px"
                                url={feature.icon}
                                name={feature?.name || feature?.title || feature?.company || ''}
                                defaultMode="initials"
                                border="circle"
                                variant="navy"
                              />
                            )}
                            {feature.link ? (
                              <a href={feature.link} target="_blank" rel="noopener noreferrer">
                                {[feature?.name, feature?.title, feature?.company]?.filter(Boolean).join(' - ')}
                              </a>
                            ) : (
                              <label className="pl-2">
                                {[feature?.name, feature?.title, feature?.company]?.filter(Boolean).join(' - ')}
                              </label>
                            )}
                          </>
                        ) : (
                          <div>
                            <div className="flex mb-2">
                              {isFounder &&
                                (feature?.name || feature?.title || feature?.company || feature?.description) && (
                                  <Avatar
                                    size="27px"
                                    initialsTextSize="12px"
                                    url={feature?.icon}
                                    name={
                                      feature?.name || feature?.title || feature?.company || feature?.description || ''
                                    }
                                    defaultMode="initials"
                                    border="circle"
                                    variant="navy"
                                  />
                                )}
                              {feature.link ? (
                                <a href={feature.link} target="_blank" rel="noopener noreferrer">
                                  {[feature?.name, feature?.title, feature?.company]?.filter(Boolean).join(' - ')}
                                </a>
                              ) : (
                                <label className="pl-2">
                                  {[feature?.name, feature?.title, feature?.company]?.filter(Boolean).join(' - ')}
                                </label>
                              )}
                            </div>
                            <p className="">{feature?.description}</p>
                          </div>
                        )}
                      </CartSubContent>
                    )}
                  </>
                )}
              </Fragment>
            )}
          </Fragment>
        )
      })}
    </div>
  )
}
